import { createClient, FailedResponse } from '@citrite/http';
import { logError } from 'logger';
import { SettingDefinition, SettingValue, UpdateSettingValueRequest } from './types';

async function callWithError<T>(fn: () => Promise<T>, errorMessage?: string) {
	let response: T;
	try {
		response = await fn();
	} catch (error) {
		const failedResponse = error as FailedResponse;
		logError(failedResponse, {
			customMessage: errorMessage,
		});

		throw failedResponse;
	}

	return response;
}

export interface PluginUserSettingsApi {
	getSettingDefinitions(): Promise<SettingDefinition[]>;
	getAllSettingValues(includeDefinitions?: boolean): Promise<SettingValue[]>;
	getSettingValue(key: string): Promise<SettingValue>;
	setSettingValue(req: UpdateSettingValueRequest): Promise<SettingValue>;
	setSettingValues(req: UpdateSettingValueRequest[]): Promise<SettingValue[]>;
}

export function createPluginUserSettingsApi(
	pluginBaseUrl: string,
	userSettingsApiVersion = 'v1'
): PluginUserSettingsApi {
	const httpClient = createClient({
		baseURL: `${pluginBaseUrl}/api/platform/contracts/settings/${userSettingsApiVersion}`,
		timeout: 60000,
		headers: {
			'x-bff-csrf': 'true',
		},
	});

	return {
		async getSettingDefinitions(): Promise<SettingDefinition[]> {
			return callWithError(async () => {
				return await httpClient.get('definitions');
			}, 'Unable to get setting definitions');
		},
		async getAllSettingValues(includeDefinitions?: boolean): Promise<SettingValue[]> {
			return callWithError(async () => {
				return await httpClient.get(
					`values?includeDefinitions=${includeDefinitions ?? false}`
				);
			}, 'Unable to get setting values');
		},
		async getSettingValue(key: string): Promise<SettingValue> {
			return callWithError(async () => {
				return await httpClient.get(`values/${key}`);
			}, 'Unable to get setting value');
		},
		async setSettingValue(req: UpdateSettingValueRequest): Promise<SettingValue> {
			return callWithError(async () => {
				return await httpClient.post('value', req);
			}, 'Unable to set setting value');
		},
		async setSettingValues(
			requests: UpdateSettingValueRequest[]
		): Promise<SettingValue[]> {
			return callWithError(async () => {
				return await httpClient.post('values', requests);
			}, 'Unable to set setting values');
		},
	};
}
